import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Form, Button, Table, Collapse } from 'react-bootstrap-v5';

export default function HoldPeriod() {
  const { id } = useParams();

  const [holdData, setHoldData] = useState({
    start_hold_period: '',
    end_hold_period: '',
    total_days: '',
    reason_for_hold: '',
    reason_for_hold_others: ''
  });

  const [holds, setHolds] = useState([]);
  const [status, setStatus] = useState('');
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(true);

  const loadAllHolds = useCallback(async (headers) => {
    const url = `${process.env.REACT_APP_API_URL}/getallholds/${id}`;
    try {
      const res = await axios.get(url, { headers });
      setHolds(res.data);
    } catch (err) {
      console.log("Error loading holds:", err);
    }
  }, [id]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      return;
    }

    const headers = {
      Authorization: `Bearer ${token}`,
    };

    loadAllHolds(headers);
  }, [id, loadAllHolds, editId]);

  useEffect(() => {
    if (holds.length === 0) {
      setIsFormOpen(true);
    } else {
      setIsFormOpen(false);
    }
  }, [holds.length]);

  const validateFields = () => {
    const requiredFields = {
      'Start Hold Period': holdData.start_hold_period,
      'End Hold Period': holdData.end_hold_period,
      'Total Days': holdData.total_days,
      'Reason for Hold': holdData.reason_for_hold
    };

    const emptyFields = Object.entries(requiredFields)
      .filter(([_, value]) => !value)
      .map(([fieldName, _]) => fieldName);

    if (emptyFields.length > 0) {
      setError(`Please fill in all required fields: ${emptyFields.join(', ')}`);
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateFields()) return;

    const token = localStorage.getItem("token");
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    const url = `${process.env.REACT_APP_API_URL}/saveholds/${id}`;
    setStatus("saving...");
    setError("");

    try {
      await axios.post(url, holdData, { headers });
      setStatus("saved");
      setError("");
      await loadAllHolds(headers);
      
      setHoldData({
        start_hold_period: '',
        end_hold_period: '',
        total_days: '',
        reason_for_hold: '',
        reason_for_hold_others: ''
      });
    } catch (err) {
      console.log(err);
      setError(err.response?.data?.error || "error saving data");
      setStatus("");
    }
  };

  const handleUpdate = async () => {
    if (!validateFields()) return;

    const token = localStorage.getItem("token");
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    const editUrl = `${process.env.REACT_APP_API_URL}/editholds/${id}/${editId}`;
    try {
      await axios.put(editUrl, holdData, { headers });
      
      setHoldData({
        start_hold_period: '',
        end_hold_period: '',
        total_days: '',
        reason_for_hold: '',
        reason_for_hold_others: ''
      });
      
      setEditId(null);
      await loadAllHolds(headers);
    } catch (err) {
      console.log("Error editing hold period:", err);
      setError("Error editing hold period");
    }
  };

  const handleDelete = async (holdId) => {
    const token = localStorage.getItem("token");
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    const url = `${process.env.REACT_APP_API_URL}/deleteholds/${holdId}`;

    try {
      await axios.delete(url, { headers });
      loadAllHolds(headers);
    } catch (err) {
      console.log("Error deleting hold period:", err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="container">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-end mb-3">
            <Button 
              onClick={() => setIsFormOpen(!isFormOpen)}
              variant="outline-primary"
              size="sm"
            >
              {isFormOpen ? 'Hide Form' : 'Add Hold Period'}
            </Button>
          </div>

          <Collapse in={isFormOpen}>
            <div>
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Start Hold Period</Form.Label>
                    <Form.Control
                      type="date"
                      value={holdData.start_hold_period}
                      onChange={(e) =>
                        setHoldData({ ...holdData, start_hold_period: e.target.value })
                      }
                      required
                    />
                  </Form.Group>
                </div>

                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>End Hold Period</Form.Label>
                    <Form.Control
                      type="date"
                      value={holdData.end_hold_period}
                      onChange={(e) =>
                        setHoldData({ ...holdData, end_hold_period: e.target.value })
                      }
                      required
                    />
                  </Form.Group>
                </div>

                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Total Days</Form.Label>
                    <Form.Control
                      type="number"
                      value={holdData.total_days}
                      onChange={(e) =>
                        setHoldData({ ...holdData, total_days: e.target.value })
                      }
                      required
                    />
                  </Form.Group>
                </div>

                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Reason for Hold</Form.Label>
                    <Form.Select
                      value={holdData.reason_for_hold}
                      onChange={(e) =>
                        setHoldData({ 
                          ...holdData, 
                          reason_for_hold: e.target.value,
                          reason_for_hold_others: e.target.value === 'Other' ? holdData.reason_for_hold_others : ''
                        })
                      }
                      required
                    >
                      <option value="">Please Select</option>
                      <option value="Seeing Someone">Seeing Someone</option>
                      <option value="In A Relationship">In A Relationship</option>
                      <option value="Too Busy With Work">Too Busy With Work</option>
                      <option value="Attending To Personal Matters">Attending To Personal Matters</option>
                      <option value="Travelling Overseas">Travelling Overseas</option>
                      <option value="Other">Other</option>
                    </Form.Select>
                  </Form.Group>
                </div>

                {holdData.reason_for_hold === 'Other' && (
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Specify Other Reason</Form.Label>
                      <Form.Control
                        type="text"
                        value={holdData.reason_for_hold_others}
                        onChange={(e) =>
                          setHoldData({ ...holdData, reason_for_hold_others: e.target.value })
                        }
                        required
                      />
                    </Form.Group>
                  </div>
                )}

                <div className="col-md-12">
                  {editId ? 
                    <Button onClick={handleUpdate} style={{ marginTop: "20px" }}>Update</Button> : 
                    <Button onClick={handleSave} style={{ marginTop: "20px" }}>Save</Button>
                  }
                  <div className="status">{status}</div>
                  <div className="error" style={{ color: 'red' }}>{error}</div>
                </div>
              </div>
            </div>
          </Collapse>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <h2>All Hold Periods</h2>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Total Days</th>
                <th>Reason</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {holds.map((hold) => (
                <tr key={hold.id}>
                  <td>{hold.start_hold_period}</td>
                  <td>{hold.end_hold_period}</td>
                  <td>{hold.total_days}</td>
                  <td>{hold.reason_for_hold === 'Other' ? hold.reason_for_hold_others : hold.reason_for_hold}</td>
                  <td>{formatDate(hold.created_at)}</td>
                  <td>
                    <Button className="btn btn-outline-primary btn-sm me-2" onClick={() => {
                      setEditId(hold.id);
                      setIsFormOpen(true);
                      setHoldData({
                        start_hold_period: hold.start_hold_period || '',
                        end_hold_period: hold.end_hold_period || '',
                        total_days: hold.total_days || '',
                        reason_for_hold: hold.reason_for_hold || '',
                        reason_for_hold_others: hold.reason_for_hold_others || ''
                      });
                    }}>
                      Edit
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => handleDelete(hold.id)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  );
}
